import React, { useRef, useCallback, useEffect } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  className = '',
  readOnly = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isUpdatingFromProps = useRef(false);

  // Update editor content when value prop changes
  useEffect(() => {
    if (editorRef.current && !isUpdatingFromProps.current) {
      const editor = editorRef.current;
      if (editor.innerHTML !== value) {
        isUpdatingFromProps.current = true;
        editor.innerHTML = value || '';
        isUpdatingFromProps.current = false;
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current && !isUpdatingFromProps.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (readOnly) return;

    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          document.execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          document.execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          document.execCommand('underline');
          break;
      }
    }
  }, [readOnly]);

  const executeCommand = useCallback((command: string, value?: string) => {
    if (readOnly) return;
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      handleInput();
    }
  }, [readOnly, handleInput]);

  const insertLink = useCallback(() => {
    if (readOnly) return;
    const url = prompt('Enter URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  }, [readOnly, executeCommand]);

  return (
    <div className={`rich-text-editor ${className}`}>
      {!readOnly && (
        <div className="rich-text-toolbar">
          <button
            type="button"
            onClick={() => executeCommand('bold')}
            className="toolbar-btn"
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('italic')}
            className="toolbar-btn"
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => executeCommand('underline')}
            className="toolbar-btn"
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </button>
          <div className="toolbar-separator"></div>
          <button
            type="button"
            onClick={() => executeCommand('insertUnorderedList')}
            className="toolbar-btn"
            title="Bullet List"
          >
            •
          </button>
          <button
            type="button"
            onClick={() => executeCommand('insertOrderedList')}
            className="toolbar-btn"
            title="Numbered List"
          >
            1.
          </button>
          <div className="toolbar-separator"></div>
          <button
            type="button"
            onClick={insertLink}
            className="toolbar-btn"
            title="Insert Link"
          >
            🔗
          </button>
          <button
            type="button"
            onClick={() => executeCommand('removeFormat')}
            className="toolbar-btn"
            title="Clear Formatting"
          >
            ✘
          </button>
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className="rich-text-content"
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
        style={{
          minHeight: '100px',
          padding: '12px',
          border: '1px solid #ddd',
          borderTop: readOnly ? '1px solid #ddd' : 'none',
          borderRadius: readOnly ? '4px' : '0 0 4px 4px',
          outline: 'none',
          fontSize: '14px',
          lineHeight: '1.5',
        }}
      />
    </div>
  );
};

export default RichTextEditor;